/* eslint-disable max-lines */
/* eslint-disable max-len */
import {
    Box,
    Button,
    Grid,
    GridItem,
    HStack,
    ModalBody,
    ModalFooter,
    Tag,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { Alert, FaIcon } from '@acp/core-ui';
import { faAt, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { SubmitBookingFormSchemaValues } from './form-scheme';
import { PropertyAddressCard } from '../../../property/components/property-address-card';
import { ConfirmBookingCard } from './confirm-booking-card';

export function ConfirmBookingScreen({
    propertyId,
    onSubmit,
    onBack,
    isLoading,
    error,
    confirmButtonText,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onSubmit: (vals: SubmitBookingFormSchemaValues) => void;
    onBack: () => void;
    isLoading: boolean;
    error: Error | null;
}) {
    const { getValues, handleSubmit } = useFormContext<SubmitBookingFormSchemaValues>();
    const { contacts, nominatedContact } = getValues('oasBooking');

    const bookingContacts = useMemo(() => {
        let contactsCopy = [...contacts];

        if (nominatedContact.isEnabled) {
            const changedContacts = contacts.map((contact) => ({
                ...contact,
                isPrimaryContact: false,
            }));

            contactsCopy = [
                ...changedContacts,
                {
                    name: nominatedContact.name,
                    email: nominatedContact.email,
                    mobile: nominatedContact.mobile,
                    isPrimaryContact: true,
                },
            ];
        }

        contactsCopy.sort((a, b) => Number(b.isPrimaryContact) - Number(a.isPrimaryContact));

        return contactsCopy;
    }, [contacts, nominatedContact]);

    return (
        <>
            <ModalBody>
                <PropertyAddressCard w="full" mb={5} propertyId={propertyId}>
                    <ConfirmBookingCard />
                </PropertyAddressCard>

                <Box>
                    <VStack alignItems="flex-start" mb={4}>
                        <Text fontSize="lg" fontWeight={500}>
                            Contacts
                        </Text>
                        <Text fontSize="md">
                            This booking will be made on behalf of the tenant. An SMS and email
                            confirmation will be sent to the recipients below once confirmed.
                        </Text>
                    </VStack>

                    <HStack w="full" align="left">
                        <Grid
                            templateColumns={{ base: 'auto', md: 'repeat(2, 1fr)' }}
                            gap={3}
                            h="auto"
                            w={{ base: 'full', md: 'auto' }}
                        >
                            {bookingContacts?.map((contact) => (
                                <GridItem w="100%" key={contact.email}>
                                    <VStack
                                        w="full"
                                        h="full"
                                        alignItems="stretch"
                                        borderColor="muted.50"
                                        borderWidth={1}
                                        p={4}
                                        direction="column"
                                        minW={250}
                                        rounded="md"
                                        shadow="sm"
                                        alignContent="start"
                                    >
                                        <Text maxW={180} fontWeight="bold">{contact.name}</Text>
                                        <HStack>
                                            <FaIcon icon={faPhone} color="muted.500" />
                                            <Text>{contact.mobile !== '' ? contact.mobile : 'No phone number'}</Text>
                                        </HStack>
                                        <HStack pb={2}>
                                            <FaIcon icon={faAt} color="muted.500" />
                                            <Text maxW={200}>{contact.email}</Text>
                                        </HStack>
                                        {contact.isPrimaryContact && (
                                            <Tag w="fit-content" size="sm" variant="solid" colorScheme="secondary">
                                                Primary contact
                                            </Tag>
                                        )}
                                    </VStack>
                                </GridItem>
                            ))}
                        </Grid>
                    </HStack>
                </Box>

                {/* Errors from server */}
                {
                    error !== null && (
                        <Alert title="An error occurred while submitting your booking request. Please try again." mt={5}>
                            {error.message}
                        </Alert>
                    )
                }
            </ModalBody>

            <ModalFooter justifyContent="space-between">
                <Button size="md" colorScheme="muted" variant="ghost" onClick={onBack}>
                    Back
                </Button>
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                >
                    {confirmButtonText ?? 'Confirm'}
                </Button>
            </ModalFooter>
        </>
    );
}
